import { useQuery } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { freelancerService } from "@/lib/api/services/freelancer.service";

/**
 * Lightweight hook to resolve user_id to freelancer profile ID
 * This creates a small, cacheable mapping that we can use to maintain
 * a single source of truth keyed by profile ID
 */
export function useFreelancerProfileId(userId?: string) {
  const { user } = useUser();
  const isCurrentUser = userId === user?.id;

  const { data: profileIdData, isLoading, error } = useQuery({
    queryKey: ["freelancer-profile-id", userId],
    queryFn: async () => {
      if (!userId) throw new Error("No user ID");

      // If it's the current user, use the API endpoint
      if (isCurrentUser) {
        try {
          const result = await freelancerService.getMyProfileId();
          return result.id;
        } catch (err: any) {
          // If profile doesn't exist, return null
          if (err.code === 'NOT_FOUND' || err.status === 404) {
            return null;
          }
          throw err;
        }
      }

      // For other users, we'd need a different endpoint or keep using Supabase
      // For now, return null and let the caller handle it
      // TODO: Add GET /api/v1/profiles/freelancer/user/:userId endpoint if needed
      return null;
    },
    enabled: !!userId && isCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false, // Prevent duplicate calls in React Strict Mode
    refetchOnWindowFocus: false, // Profile ID rarely changes
  });

  return { profileId: profileIdData ?? null, isLoading, error };
}